// Daily operations — the "what does today still owe me" layer over the three
// agent operations. Pure logic: no JSX, no React, no network, no model call, no
// Math.random anywhere that decides an outcome. Node-testable as-is.
//
// ── WHAT THIS IS ────────────────────────────────────────────────────────────
// The workspace has three daily operations. Each one is a task that is either
// done today or not:
//
//   initial-outreach-50  Initial outreach — 50 new leads   (agentRun pipeline)
//   followup-50          Daily Tasks — the rep's HubSpot tasks due
//   reply-open-threads   Follow-Up — the customer lanes
//
// This module owns ONLY the completion ledger and the reminder state derived
// from it. It does not run anything, does not pick leads, and does not know how
// any operation works. Task ids are deliberately IDENTICAL to the operation ids
// in lib/agentRun.js so a caller can hand either one to the other; the catalog
// is duplicated here rather than imported so this file stays free of the
// pipeline's transitive imports (which reach into JSX) and stays node-testable.
// If an operation id ever changes in agentRun.js, change it here in the same
// commit — TASK_IDS below is the join key.
//
// ── THE CONTRACT ────────────────────────────────────────────────────────────
//
//   listDailyTasks({ now })      → [{ id, label, blurb, order, operationId,
//                                     done, doneAt, receipt }]
//                                  Every task, in display order, with today's
//                                  completion state resolved.
//
//   isTaskDone(id, { now })      → boolean, for today only.
//
//   getDailyTask(id)             → the static catalog entry or null.
//
//   markTaskDone(id, receipt, { now, ok })
//                                → { taskId, dayKey, at, receipt } | null
//                                  Records a completion for TODAY. Returns null
//                                  and writes nothing when the id is unknown or
//                                  ok === false.
//
//   recordRunResult(id, result, { now })
//                                → { recorded, reason, entry }
//                                  The bridge from a runOperation() return
//                                  value. `result.ok !== true` (an abandoned
//                                  run) NEVER marks the task done — it comes
//                                  back { recorded:false, reason:'abandoned' }.
//                                  A successful result contributes its receipt
//                                  (list name, campaign name, counts).
//
//   unmarkTaskDone(id, { now })  → boolean. Undo, for a UI that offers one.
//
//   dailyProgress({ now })       → { dayKey, total, doneCount, remainingCount,
//                                    done[], remaining[], state, allDone,
//                                    noneStarted, headline, late, hour,
//                                    lastActiveDay, daysSinceLastActivity }
//                                  `state` is 'all-done' | 'partial' |
//                                  'none-started' — the three faces a reminder
//                                  has to wear. `done`/`remaining` are the full
//                                  task objects so a UI can name them.
//
//   dailySummaryLine({ now })    → one honest sentence for chat / a card.
//
//   dailyHistory({ days, now })  → [{ dayKey, doneCount, total, taskIds }],
//                                  most recent first, today included.
//
//   subscribeDailyTasks(fn)      → unsubscribe. Fires after every write so a
//                                  dashboard mounted elsewhere can refresh
//                                  without a remount (same idiom as events.js).
//
// ── RULES THIS FILE KEEPS ───────────────────────────────────────────────────
// 1. TODAY IS COMPUTED AT CALL TIME. Never at module load. A tab left open
//    overnight would otherwise keep reporting yesterday's ledger as today's,
//    and yesterday's finished work would read as done forever. Every public
//    function takes an injectable `now` for exactly this reason (and so the
//    tests can cross midnight without sleeping).
// 2. LOCAL CALENDAR DAY. The day key is built from local Y/M/D — never
//    toISOString(), which is UTC and rolls over at the wrong moment for
//    everyone west of Greenwich. "Done today" must mean the day on the user's
//    wall clock.
// 3. PER USER. Keyed through scopedKey(), like every other client-side store, so
//    one account's finished day is not another account's.
// 4. HONEST COUNTS. Receipts store what a run actually produced. Nothing here
//    invents or rounds a number.
// 5. NEVER THROWS INTO A RENDER. Corrupt, absent, or unwritable localStorage
//    degrades to "nothing done" — the safe direction: a user is reminded to do
//    work they already did, rather than told they are finished when they are
//    not.
// 6. BOUNDED. The ledger keeps a rolling window of the most recent
//    HISTORY_DAYS days and prunes on every write.
import { scopedKey } from './userScope.js'

const STORAGE_BASE = 'revailosales.dailyTasks'
const storageKey = () => scopedKey(STORAGE_BASE)

// How many local days of completions to retain. Long enough to draw a streak or
// a fortnight of history, short enough that the blob can never grow without
// bound: 3 tasks/day means the ceiling is ~90 small entries, forever.
export const HISTORY_DAYS = 45

// Broadcast name for live surfaces. Mirrors events.js' 'revailosales:event'.
export const DAILY_TASKS_EVENT = 'revailosales:dailytasks'

// ── the catalog ─────────────────────────────────────────────────────────────
// Order is display order (owner's ask, 2026-09-23): Daily Tasks first, then
// Follow-Up, then initial outreach — the tasks the rep already typed lead the
// morning, the customers come next, and new leads are the day's last job.
export const DAILY_TASKS = Object.freeze([
  Object.freeze({
    id: 'followup-50',
    operationId: 'followup-50',
    order: 1,
    label: 'Daily Tasks',
    blurb: 'The rep’s own HubSpot tasks — overdue, or due today — in their own wording.',
  }),
  Object.freeze({
    id: 'reply-open-threads',
    operationId: 'reply-open-threads',
    order: 2,
    label: 'Follow-Up',
    blurb:
      'The customer follow-up in four sections: inactive users, trials ending within 7 days, expired trials and payment failed.',
  }),
  Object.freeze({
    id: 'initial-outreach-50',
    operationId: 'initial-outreach-50',
    order: 3,
    label: 'Initial outreach — 50 new leads',
    blurb: 'Pick 50 uncontacted, reachable leads and draft the opening email for each.',
  }),
])

export const TASK_IDS = Object.freeze(DAILY_TASKS.map((t) => t.id))
const TASK_BY_ID = new Map(DAILY_TASKS.map((t) => [t.id, t]))

export function getDailyTask(taskId) {
  return TASK_BY_ID.get(taskId) || null
}

// ── the day key ─────────────────────────────────────────────────────────────

const pad = (n) => String(n).padStart(2, '0')

// 'YYYY-MM-DD' in LOCAL time. Called per operation, never memoised: see rule 1.
export function dayKeyOf(now = new Date()) {
  const d = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// The current local calendar day. `todayKey()` with no argument is always fresh.
export function todayKey(now = new Date()) {
  return dayKeyOf(now)
}

const DAY_KEY_RE = /^\d{4}-\d{2}-\d{2}$/
const DAY_MS = 86400000

// Day-key arithmetic done on a local midnight Date, so a key never drifts
// across a DST boundary the way `key - n*86400000` on a raw parse would.
function shiftDayKey(key, deltaDays) {
  const [y, m, d] = key.split('-').map(Number)
  const at = new Date(y, m - 1, d + deltaDays)
  return dayKeyOf(at)
}

function daysBetweenKeys(fromKey, toKey) {
  const [fy, fm, fd] = fromKey.split('-').map(Number)
  const [ty, tm, td] = toKey.split('-').map(Number)
  const a = new Date(fy, fm - 1, fd).getTime()
  const b = new Date(ty, tm - 1, td).getTime()
  return Math.round((b - a) / DAY_MS)
}

// ── storage ─────────────────────────────────────────────────────────────────
// Shape on disk:
//   { version: 1, days: { 'YYYY-MM-DD': { '<taskId>': { at, receipt } } } }
// Anything that is not exactly that is discarded rather than repaired: a
// half-understood ledger is worse than an empty one, because it would claim
// work is finished on evidence nobody can read.

const STORE_VERSION = 1

function safeStorage() {
  try {
    // Missing entirely under SSR / node / a locked-down browser.
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function normalizeEntry(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const at = typeof raw.at === 'string' && raw.at ? raw.at : ''
  if (!at) return null
  return { at, receipt: normalizeReceipt(raw.receipt) }
}

function read() {
  const store = safeStorage()
  if (!store) return { version: STORE_VERSION, days: {} }
  let parsed
  try {
    const raw = store.getItem(storageKey())
    parsed = raw ? JSON.parse(raw) : null
  } catch {
    return { version: STORE_VERSION, days: {} }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { version: STORE_VERSION, days: {} }
  }
  const rawDays = parsed.days
  if (!rawDays || typeof rawDays !== 'object' || Array.isArray(rawDays)) {
    return { version: STORE_VERSION, days: {} }
  }
  const days = {}
  for (const [dayKey, tasks] of Object.entries(rawDays)) {
    if (!DAY_KEY_RE.test(dayKey)) continue
    if (!tasks || typeof tasks !== 'object' || Array.isArray(tasks)) continue
    const kept = {}
    for (const [taskId, entry] of Object.entries(tasks)) {
      // An id we no longer recognise is dropped, not carried: it can only make
      // a progress count wrong in the flattering direction.
      if (!TASK_BY_ID.has(taskId)) continue
      const norm = normalizeEntry(entry)
      if (norm) kept[taskId] = norm
    }
    if (Object.keys(kept).length) days[dayKey] = kept
  }
  return { version: STORE_VERSION, days }
}

// Keep only the HISTORY_DAYS most recent day keys. Lexicographic sort is a
// correct chronological sort for zero-padded 'YYYY-MM-DD'.
function prune(days) {
  const keys = Object.keys(days).sort()
  if (keys.length <= HISTORY_DAYS) return days
  const keep = new Set(keys.slice(keys.length - HISTORY_DAYS))
  const out = {}
  for (const k of keys) if (keep.has(k)) out[k] = days[k]
  return out
}

function write(state) {
  const next = { version: STORE_VERSION, days: prune(state.days || {}) }
  const store = safeStorage()
  if (store) {
    try {
      store.setItem(storageKey(), JSON.stringify(next))
    } catch {
      // Quota or private mode. The caller still gets a truthful return value
      // for this call; the next read just won't see it. Better than throwing
      // out of a click handler.
    }
  }
  try {
    window.dispatchEvent(new CustomEvent(DAILY_TASKS_EVENT, { detail: { days: next.days } }))
  } catch {
    /* SSR / no window */
  }
  return next
}

// ── receipts ────────────────────────────────────────────────────────────────
// What a completed run left behind, in the few fields a card can render. A
// whitelist, clamped: this blob is persisted, and an operation that handed us
// its whole result object would put 50 lead rows in localStorage forever.

const MAX_TEXT = 160
const MAX_COUNTS = 8

const text = (v) => {
  const s = String(v ?? '').trim()
  return s ? s.slice(0, MAX_TEXT) : ''
}

const count = (v) => {
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
}

export function normalizeReceipt(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out = {}
  const c = count(raw.count)
  if (c !== null) out.count = c
  for (const field of ['summary']) {
    const v = text(raw[field])
    if (v) out[field] = v
  }
  // Free-form named tallies ("drafted", "due", "threads") — numbers only, so a
  // UI can render "12 drafted" without trusting a string from anywhere.
  if (raw.counts && typeof raw.counts === 'object' && !Array.isArray(raw.counts)) {
    const counts = {}
    for (const [k, v] of Object.entries(raw.counts)) {
      if (Object.keys(counts).length >= MAX_COUNTS) break
      const n = count(v)
      if (n !== null) counts[text(k)] = n
    }
    if (Object.keys(counts).length) out.counts = counts
  }
  return out
}

// ── reading today ───────────────────────────────────────────────────────────

export function listDailyTasks({ now = new Date() } = {}) {
  const dayKey = dayKeyOf(now)
  const today = read().days[dayKey] || {}
  return DAILY_TASKS.map((task) => {
    const entry = today[task.id] || null
    return {
      ...task,
      dayKey,
      done: Boolean(entry),
      doneAt: entry ? entry.at : null,
      receipt: entry ? entry.receipt : null,
    }
  })
}

export function isTaskDone(taskId, { now = new Date() } = {}) {
  if (!TASK_BY_ID.has(taskId)) return false
  return Boolean(read().days[dayKeyOf(now)]?.[taskId])
}

// ── recording ───────────────────────────────────────────────────────────────

/**
 * Mark a task done for TODAY (the local day containing `now`).
 *
 * Refuses — writes nothing, returns null — for an unknown task id or when
 * `ok` is explicitly false, so a caller can pipe a run result straight through
 * without first branching on it.
 *
 * Re-marking an already-done task overwrites the timestamp and receipt with
 * the newer run's. Two runs of the same operation in one day are one done task
 * with the later receipt, not two.
 */
export function markTaskDone(taskId, receipt = {}, { now = new Date(), ok = true } = {}) {
  if (!TASK_BY_ID.has(taskId)) return null
  if (ok === false) return null
  const dayKey = dayKeyOf(now)
  const at = (now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date()).toISOString()
  const entry = { at, receipt: normalizeReceipt(receipt) }
  const state = read()
  state.days[dayKey] = { ...(state.days[dayKey] || {}), [taskId]: entry }
  write(state)
  return { taskId, dayKey, ...entry }
}

export function unmarkTaskDone(taskId, { now = new Date() } = {}) {
  if (!TASK_BY_ID.has(taskId)) return false
  const dayKey = dayKeyOf(now)
  const state = read()
  const day = state.days[dayKey]
  if (!day || !day[taskId]) return false
  delete day[taskId]
  if (!Object.keys(day).length) delete state.days[dayKey]
  write(state)
  return true
}

/**
 * Record the outcome of a runOperation()-shaped result.
 *
 * A run that came back `ok:false` (abandoned — nothing was built) does NOT
 * count as the day's work and is never marked done. That is the whole point of
 * this function existing next to markTaskDone.
 *
 * @returns {{recorded:boolean, reason:string, entry:object|null}}
 *          reason: 'ok' | 'abandoned' | 'no-result' | 'unknown-task'
 */
export function recordRunResult(taskId, result, { now = new Date() } = {}) {
  if (!TASK_BY_ID.has(taskId)) return { recorded: false, reason: 'unknown-task', entry: null }
  if (!result || typeof result !== 'object') {
    return { recorded: false, reason: 'no-result', entry: null }
  }
  // Two shapes of non-success, and only one of them means work is outstanding.
  // `nothingToDo` is set by agentRun's abandon() when the operation ran
  // correctly and found nothing waiting — no follow-up due, no thread needing a
  // reply. That IS the day's task discharged: without this, a workspace with a
  // quiet day could never clear the reminder, and a nag you cannot satisfy is a
  // nag people learn to ignore. A BLOCKED run (Gmail not connected, queue
  // unreadable, nothing readable enough to judge) is still work you owe.
  if (result.ok !== true && result.nothingToDo !== true) {
    return { recorded: false, reason: 'abandoned', entry: null }
  }

  const selected = Array.isArray(result.selected) ? result.selected.length : null
  // A BRIEFING (Daily Tasks, Follow-Up — `result.briefing` set) selects no
  // leads, and "0 selected" on its tile was a wrong sentence, not a count.
  // Those results carry `summary` instead; the count is left off unless the
  // run set one itself.
  const briefing = typeof result.briefing === 'string' && result.briefing !== ''
  const entry = markTaskDone(
    taskId,
    {
      count: result.count ?? (briefing ? undefined : selected) ?? undefined,
      summary: result.summary,
      counts: result.counts,
    },
    { now },
  )
  return { recorded: Boolean(entry), reason: entry ? 'ok' : 'no-result', entry }
}

// ── reminder state ──────────────────────────────────────────────────────────

// Local hour at or after which an unfinished day is worth nagging about. Not a
// deadline — the operations have none — just the point where "still outstanding"
// becomes the more useful thing to say than "outstanding".
export const LATE_HOUR = 15

/**
 * Everything a reminder surface needs, resolved for the local day containing
 * `now`. Counts are literal: `remainingCount` is the number of task objects in
 * `remaining`, and nothing is rounded or assumed.
 */
export function dailyProgress({ now = new Date() } = {}) {
  const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date()
  const tasks = listDailyTasks({ now: at })
  const done = tasks.filter((t) => t.done)
  const remaining = tasks.filter((t) => !t.done)
  const dayKey = dayKeyOf(at)

  const state =
    done.length === tasks.length ? 'all-done' : done.length === 0 ? 'none-started' : 'partial'

  // When work last happened at all — the honest answer to "how long has this
  // been slipping". null when the retained window holds nothing.
  const dayKeys = Object.keys(read().days).sort()
  const lastActiveDay = dayKeys.length ? dayKeys[dayKeys.length - 1] : null

  return {
    dayKey,
    total: tasks.length,
    doneCount: done.length,
    remainingCount: remaining.length,
    tasks,
    done,
    remaining,
    state,
    allDone: state === 'all-done',
    noneStarted: state === 'none-started',
    hour: at.getHours(),
    late: state !== 'all-done' && at.getHours() >= LATE_HOUR,
    lastActiveDay,
    daysSinceLastActivity: lastActiveDay ? daysBetweenKeys(lastActiveDay, dayKey) : null,
    headline: headlineFor(state, done.length, tasks.length),
  }
}

function headlineFor(state, doneCount, total) {
  if (state === 'all-done') return `All ${total} daily operations done`
  if (state === 'none-started') return `${total} daily operations still to run`
  const left = total - doneCount
  return `${doneCount} of ${total} done — ${left} still to run`
}

// One sentence, safe to drop into chat or a card. Names what is left, because
// "2 outstanding" is not actionable and "follow-ups and replies" is.
export function dailySummaryLine({ now = new Date() } = {}) {
  const p = dailyProgress({ now })
  if (p.allDone) return `Today’s work is done — all ${p.total} daily operations have run.`
  const names = p.remaining.map((t) => t.label)
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  if (p.noneStarted) return `Nothing has run today. Still to do: ${list}.`
  return `${p.doneCount} of ${p.total} done today. Still to do: ${list}.`
}

// ── history ─────────────────────────────────────────────────────────────────

/**
 * The last `days` local days, most recent first, today at index 0. Days with no
 * completions are included as zeroes — a gap is information, and a chart that
 * skips empty days lies about the shape of the week.
 */
export function dailyHistory({ days = 14, now = new Date() } = {}) {
  const span = Math.max(1, Math.min(HISTORY_DAYS, Math.floor(Number(days)) || 1))
  const store = read().days
  const today = dayKeyOf(now)
  const out = []
  for (let i = 0; i < span; i += 1) {
    const dayKey = shiftDayKey(today, -i)
    const entries = store[dayKey] || {}
    // Ordered by the catalog, not by object insertion order, so two days with
    // the same tasks always render identically.
    const taskIds = DAILY_TASKS.filter((t) => entries[t.id]).map((t) => t.id)
    out.push({ dayKey, total: DAILY_TASKS.length, doneCount: taskIds.length, taskIds })
  }
  return out
}

// ── live updates ────────────────────────────────────────────────────────────

export function subscribeDailyTasks(fn) {
  if (typeof fn !== 'function') return () => {}
  try {
    const handler = (e) => fn(e?.detail || null)
    window.addEventListener(DAILY_TASKS_EVENT, handler)
    return () => {
      try {
        window.removeEventListener(DAILY_TASKS_EVENT, handler)
      } catch {
        /* ignore */
      }
    }
  } catch {
    return () => {}
  }
}

// Test/debug seam: drop the whole ledger for the current user.
export function clearDailyTasks() {
  write({ version: STORE_VERSION, days: {} })
}
